/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FiChevronLeft,
  FiChevronRight,
  FiGlobe,
  FiMail,
  FiUser,
  FiCheck,
} from "react-icons/fi";
import moment from "moment-timezone";
import useUserTimezone from "@/hooks/shared/useUserTimezone";
import { useDict } from "@/components/providers/LocaleProvider";

type Props = {
  /** Meeting length in minutes */
  duration?: 15 | 30 | 45 | 60;
  /** Earliest slot hour (local to chosen timezone) */
  startHour?: number; // 9
  /** Latest slot hour (exclusive) */
  endHour?: number; // 18
  /** Optional: slot step minutes (default derived from duration) */
  stepMinutes?: 15 | 30 | 60;
};

type CalendarCell = {
  /** UTC instant representing 00:00 of that day in the chosen TZ */
  date: Date;
  inCurrentMonth: boolean;
  isToday: boolean;
  isPast: boolean;
};

export const COMMON_TIMEZONES = [
  // Global
  "UTC",

  // North America
  "America/Los_Angeles", // PT
  "America/Denver", // MT
  "America/Phoenix", // Arizona (no DST)
  "America/Chicago", // CT
  "America/New_York", // ET
  "America/Toronto",
  "America/Vancouver",
  "America/Halifax",
  "America/St_Johns", // Newfoundland
  "America/Mexico_City",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "America/Argentina/Buenos_Aires",
  "America/Sao_Paulo",
  "America/Anchorage",
  "Pacific/Honolulu",

  // Europe
  "Europe/London",
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Berlin",
  "Europe/Zurich",
  "Europe/Vienna",
  "Europe/Rome",
  "Europe/Prague",
  "Europe/Warsaw",
  "Europe/Stockholm",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Helsinki",
  "Europe/Athens",
  "Europe/Bucharest",
  "Europe/Kyiv",
  "Europe/Istanbul",
  "Europe/Moscow",

  // Africa & Middle East
  "Africa/Casablanca",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Africa/Johannesburg",
  "Asia/Jerusalem",
  "Asia/Amman",
  "Asia/Beirut",
  "Asia/Baghdad",
  "Asia/Tehran",
  "Asia/Riyadh",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Dubai",
  "Asia/Muscat",

  // South & Central Asia
  "Asia/Kolkata",
  "Asia/Kathmandu", // UTC+5:45
  "Asia/Colombo",
  "Asia/Karachi",
  "Asia/Dhaka",
  "Asia/Yangon", // UTC+6:30
  "Asia/Tashkent",
  "Asia/Almaty",

  // East & Southeast Asia
  "Asia/Bangkok",
  "Asia/Ho_Chi_Minh",
  "Asia/Jakarta",
  "Asia/Kuala_Lumpur",
  "Asia/Singapore",
  "Asia/Manila",
  "Asia/Hong_Kong",
  "Asia/Taipei",
  "Asia/Shanghai",
  "Asia/Seoul",
  "Asia/Tokyo",

  // Australia & Oceania
  "Australia/Perth",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Melbourne",
  "Australia/Sydney",
  "Pacific/Auckland",
  "Pacific/Fiji",
] as const;

const minutesToLabel = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return (
    `${h > 0 ? `${h} hr` : ""}${h > 0 && m ? " " : ""}${m ? `${m} min` : ""}` ||
    "0 min"
  );
};

const sameDayInTz = (a: Date, b: Date, tz: string) =>
  moment(a).tz(tz).isSame(moment(b).tz(tz), "day");

/** Monday-first calendar matrix built in the selected time zone */
const getMonthMatrix = (
  year: number,
  month: number,
  tz: string
): CalendarCell[] => {
  const first = moment.tz(
    { year, month, day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 },
    tz
  );
  const gridStart = first.clone().startOf("isoWeek"); // Monday
  const nowTz = moment().tz(tz);
  const todaySodTz = nowTz.clone().startOf("day");

  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const m = gridStart.clone().add(i, "day").startOf("day"); // wall-clock midnight in tz
    cells.push({
      date: m.toDate(), // absolute instant
      inCurrentMonth: m.month() === first.month(),
      isToday: m.isSame(nowTz, "day"),
      isPast: m.isBefore(todaySodTz, "day"),
    });
  }
  return cells;
};

export const BookMeeting: React.FC<Props> = ({
  duration = 60,
  startHour = 9,
  endHour = 18,
  stepMinutes,
}) => {
  const t = useDict();
  const tt = t.bookMeeting;
  const info = useUserTimezone()
  // timezone
  const defaultTz =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC";

  const initialTz = (COMMON_TIMEZONES as readonly string[]).includes(
    defaultTz as any
  )
    ? defaultTz
    : "UTC";

  const [timezone, setTimezone] = useState<string>(initialTz);

  // calendar state (cursor is month in *browser* local - OK for label only)
  const now = new Date();
  const [cursor, setCursor] = useState<Date>(
    new Date(now.getFullYear(), now.getMonth(), 1)
  );

  // We store selected day as a UTC instant representing 00:00 in the chosen TZ
  const [selectedDateUtcSod, setSelectedDateUtcSod] = useState<Date | null>(
    null
  );
  // Selected slot is a UTC instant
  const [selectedSlotUtc, setSelectedSlotUtc] = useState<Date | null>(null);

  // mobile step: 0 pick date/time, 1 details, 2 success
  const [step, setStep] = useState<0 | 1 | 2>(0);

  const matrix = useMemo(
    () => getMonthMatrix(cursor.getFullYear(), cursor.getMonth(), timezone),
    [cursor, timezone]
  );

  // Slot step granularity. We expose slots every 15 min; the booking
  // overlap rule (a 60-min meeting blocks anything within its window)
  // creates the 1-hour gap around any booked slot automatically.
  const stepMin = useMemo(() => {
    if (stepMinutes) return stepMinutes;
    return 15;
  }, [stepMinutes]);

  // Booked slots fetched from the server, used to disable unavailable times.
  const [bookedSlots, setBookedSlots] = useState<
    Array<{ start: Date; end: Date }>
  >([]);

  // Fetch booked slots for the visible month whenever cursor or timezone change.
  useEffect(() => {
    const monthStart = moment
      .tz(
        {
          year: cursor.getFullYear(),
          month: cursor.getMonth(),
          day: 1,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        timezone
      )
      .startOf("day");
    const monthEnd = monthStart.clone().add(1, "month");

    const ctrl = new AbortController();
    fetch(
      `/api/book-meeting?from=${encodeURIComponent(
        monthStart.toISOString()
      )}&to=${encodeURIComponent(monthEnd.toISOString())}`,
      { signal: ctrl.signal, cache: "no-store" }
    )
      .then((r) => (r.ok ? r.json() : { bookings: [] }))
      .then((data: { bookings?: Array<{ startUtc: string; endUtc: string }> }) => {
        const list = (data.bookings ?? []).map((b) => ({
          start: new Date(b.startUtc),
          end: new Date(b.endUtc),
        }));
        setBookedSlots(list);
      })
      .catch(() => {
        /* network/abort - keep previous list */
      });

    return () => ctrl.abort();
  }, [cursor, timezone]);

  // A candidate slot conflicts if its [start, start+duration) window overlaps
  // any booked [start, end). Two intervals [a,b) and [c,d) overlap iff a<d && c<b.
  const isSlotBooked = useCallback(
    (slot: Date) => {
      const slotStart = slot.getTime();
      const slotEnd = slotStart + duration * 60_000;
      return bookedSlots.some(
        (b) => b.start.getTime() < slotEnd && b.end.getTime() > slotStart
      );
    },
    [bookedSlots, duration]
  );

  // Generate slots for the selected day in the chosen TZ → return as UTC Dates
  const timeSlotsUtc = useMemo(() => {
    if (!selectedDateUtcSod) return [];

    const dayLocal = moment(selectedDateUtcSod).tz(timezone); // this is midnight local in tz
    const y = dayLocal.year();
    const m = dayLocal.month();
    const d = dayLocal.date();

    const out: Date[] = [];
    for (
      let minutes = startHour * 60;
      minutes + duration <= endHour * 60;
      minutes += stepMin
    ) {
      const hr = Math.floor(minutes / 60);
      const min = minutes % 60;
      const slotLocal = moment.tz(
        {
          year: y,
          month: m,
          day: d,
          hour: hr,
          minute: min,
          second: 0,
          millisecond: 0,
        },
        timezone
      );
      out.push(slotLocal.toDate()); // absolute instant
    }
    return out;
  }, [selectedDateUtcSod, timezone, startHour, endHour, duration, stepMin]);

  const nextMonth = useCallback(() => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  }, []);
  const prevMonth = useCallback(() => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  }, []);

  const canGoPrev = useMemo(() => {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return cursor > start;
  }, [cursor, now]);

  const handleDateSelect = useCallback((dateUtcMidnight: Date) => {
    setSelectedDateUtcSod(dateUtcMidnight);
    setSelectedSlotUtc(null);
  }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const readyToConfirm = !!(selectedDateUtcSod && selectedSlotUtc);
  const readyToBook =
    readyToConfirm &&
    !!name.trim() &&
    !!email.trim() &&
    !!phone.trim() &&
    !!notes.trim();

  const handleConfirmContinue = async () => {
    if (!readyToConfirm) return;
    setStep(1);
  };

  const onTimezoneChange = (tz: string) => {
    if (tz !== timezone) {
      setTimezone(tz);
      setSelectedDateUtcSod(null);
      setSelectedSlotUtc(null);
      setStep(0);
      toast(tt.timezoneChanged, {
        icon: "🌍",
      });
    }
  };

  // Filter past times for “today” in chosen TZ
  const selectedIsTodayInTz = selectedDateUtcSod
    ? moment(selectedDateUtcSod)
        .tz(timezone)
        .isSame(moment().tz(timezone), "day")
    : false;

  const visibleSlotsUtc = useMemo(
    () =>
      timeSlotsUtc.filter((s) => {
        if (selectedIsTodayInTz && s < new Date()) return false;
        return !isSlotBooked(s);
      }),
    [timeSlotsUtc, selectedIsTodayInTz, isSlotBooked]
  );

  // UI helpers
  const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const headerLabel = new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(cursor);

  const selectedFullLabel =
    selectedSlotUtc &&
    moment(selectedSlotUtc)
      .tz(timezone)
      .format("dddd, MMMM D, YYYY h:mm A (z)");

  const submit = async () => {
    if (!selectedSlotUtc) return;
    setSubmitting(true);

    const payload = {
      name,
      email,
      phone,
      timezone, // client's selected IANA zone
      // Authoritative instant (UTC)
      slotUtc: moment(selectedSlotUtc).utc().toISOString(),
      // Helpful local context
      slotLocalISO: moment(selectedSlotUtc)
        .tz(timezone)
        .format("YYYY-MM-DDTHH:mm:ssZ"),
      slotLocalPretty: moment(selectedSlotUtc)
        .tz(timezone)
        .format("dddd, MMMM D, YYYY h:mm A (z)"),
      duration,
      notes,
      // Legacy fallback for older backends
      dateISO: moment(selectedSlotUtc).utc().toISOString(),
      userTimezone: info.label,
    };

    toast.loading("Booking your meeting...", { id: "booking" });
    try {
      const response = await fetch("/api/book-meeting", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data: any = {};
      try {
        data = await response.json();
      } catch {
        /* ignore */
      }

      if (response.ok) {
        toast.success(data?.message || "Booking request sent successfully!", {
          id: "booking",
        });
        // Mark the just-booked slot as taken locally so it disables
        // immediately without waiting for a refetch.
        setBookedSlots((prev) => [
          ...prev,
          {
            start: new Date(selectedSlotUtc),
            end: new Date(selectedSlotUtc.getTime() + duration * 60_000),
          },
        ]);
        setStep(2);
      } else {
        throw new Error(data?.error || `Request failed (${response.status})`);
      }
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong.", { id: "booking" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="w-full max-w-[var(--spacing-maxwidth)] mx-auto bg-[#14171f] rounded-xl border border-white/10 shadow-soft overflow-hidden">
      <div className="grid lg:grid-cols-[340px,1fr]">
        <div className="p-6 lg:p-8 text-white">
          {/* Header */}
          {step !== 2 && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center gap-2 text-sm">
                  <FiGlobe className="text-[#846dff]" aria-hidden="true" />
                  <label className="sr-only" htmlFor="tz">
                    {tt.timezoneLabel}
                  </label>
                  <select
                    id="tz"
                    className="bg-[#1c212b] border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                    value={timezone}
                    onChange={(e) => onTimezoneChange(e.target.value)}
                    aria-label={tt.timezonePlaceholder}
                  >
                    {[defaultTz, ...COMMON_TIMEZONES]
                      .filter((v, i, a) => a.indexOf(v) === i)
                      .map((tz) => (
                        <option key={tz} value={tz} className="bg-[#14171f]">
                          {tz}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {selectedSlotUtc && (
                <div
                  className="text-right text-sm text-white/80"
                  aria-live="polite"
                >
                  {tt.timezoneSelected}&nbsp;
                  <span className="font-semibold text-white">
                    {selectedFullLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ---------- DESKTOP: 3 columns ---------- */}
          {step !== 2 && (
            <div className="hidden lg:grid gap-6 lg:grid-cols-[320px,1fr,360px]">
              {/* Calendar */}
              <div
                className="bg-[#1c212b] border border-white/10 rounded-xl p-4"
                role="grid"
                aria-label={tt.calendarLabel}
              >
                <div className="flex items-center justify-between">
                  <button
                    className="p-2 rounded-md hover:bg-white/5 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                    onClick={prevMonth}
                    disabled={!canGoPrev}
                    aria-label={tt.previousMonth}
                  >
                    <FiChevronLeft aria-hidden="true" />
                  </button>
                  <div className="text-sm font-medium text-white">
                    {headerLabel}
                  </div>
                  <button
                    className="p-2 rounded-md hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                    onClick={nextMonth}
                    aria-label={tt.nextMonth}
                  >
                    <FiChevronRight aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-7 gap-1 text-center text-xs text-white/70">
                  {weekdayLabels.map((w) => (
                    <div key={w} className="py-1">
                      {w}
                    </div>
                  ))}
                </div>

                <div className="mt-2 grid grid-cols-7 gap-1">
                  {matrix.map((cell) => {
                    const isSelected = selectedDateUtcSod
                      ? sameDayInTz(selectedDateUtcSod, cell.date, timezone)
                      : false;

                    const base =
                      "relative h-10 rounded-md text-sm select-none flex items-center justify-center transition";
                    const inMonth = cell.inCurrentMonth;
                    const past = cell.isPast && !isSelected;
                    const today = cell.isToday;

                    return (
                      <button
                        key={cell.date.toISOString()}
                        disabled={past}
                        aria-pressed={isSelected}
                        onClick={() => handleDateSelect(cell.date)}
                        className={[
                          base,
                          inMonth ? "text-white/90" : "text-white/50",
                          past
                            ? "opacity-30 cursor-not-allowed"
                            : "hover:bg-[#846dff]/10",
                          isSelected
                            ? "bg-[#846dff]/20 ring-1 ring-[#846dff]"
                            : "border border-white/10",
                          today && !isSelected ? "border-[#846dff]/40" : "",
                        ].join(" ")}
                      >
                        <span className="z-10">
                          {moment(cell.date).tz(timezone).format("D")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time slots */}
              <div className="bg-[#1c212b] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-white">
                    {tt.availableTimes}
                  </div>
                  <div className="text-xs text-white/70">
                    {minutesToLabel(duration)} {tt.each}
                  </div>
                </div>

                {!selectedDateUtcSod && (
                  <div className="mt-6 text-white/75 text-sm">
                    {tt.pickDateHint}
                  </div>
                )}

                {!!selectedDateUtcSod && (
                  <div className="mt-4 max-h-[460px] p-2 overflow-y-auto pr-1 space-y-2 no-scrollbar">
                    {visibleSlotsUtc.map((slot) => {
                      const isActive =
                        selectedSlotUtc?.getTime() === slot.getTime();
                      return (
                        <button
                          key={slot.getTime()}
                          onClick={() => setSelectedSlotUtc(slot)}
                          className={[
                            "w-full justify-between flex items-center rounded-md px-4 py-3 text-sm transition",
                            "border border-white/10 hover:bg-[#846dff]/10",
                            isActive
                              ? "bg-[#846dff]/20 ring-1 ring-[#846dff]"
                              : "",
                          ].join(" ")}
                        >
                          <span className="text-white">
                            {moment(slot).tz(timezone).format("h:mm A")}
                          </span>
                          <span className="text-xs text-white/70">
                            {isActive ? tt.selected : tt.select}
                          </span>
                        </button>
                      );
                    })}
                    {visibleSlotsUtc.length === 0 && (
                      <div className="text-sm text-white/70 px-2 py-3">
                        {tt.noFutureSlots.replace("{timezone}", timezone)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Info form (sticky) */}
              <div className="bg-[#1c212b] border border-white/10 rounded-xl p-4 lg:sticky lg:top-8 self-start">
                <p className="text-sm font-medium mb-4 text-white">
                  {tt.enterYourDetails}
                </p>

                <div className="rounded-md bg-[#14171f] border border-white/10 px-3 py-3 text-sm mb-3 text-white/90">
                  <div className="font-medium">{tt.yourSelection}</div>
                  <div className="mt-1 text-white/80 min-h-[20px]">
                    {selectedSlotUtc ? (
                      <>
                        {selectedFullLabel}
                        <div className="text-xs text-white/60 mt-1">
                          {tt.timezoneLabel}: {timezone}
                        </div>
                      </>
                    ) : (
                      <span className="text-white/60">
                        {tt.chooseDateTimeFirst}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid gap-3">
                  <div className="relative">
                    <FiUser
                      className="absolute left-3 top-3 text-white/60"
                      aria-hidden="true"
                    />
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={tt.fields.name.label}
                      className="w-full pl-10 pr-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60 disabled:opacity-50"
                      disabled={!selectedSlotUtc}
                      required
                    />
                  </div>
                  <div className="relative">
                    <FiUser
                      className="absolute left-3 top-3 text-white/60"
                      aria-hidden="true"
                    />
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={tt.fields.phone.label}
                      className="w-full pl-10 pr-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60 disabled:opacity-50"
                      disabled={!selectedSlotUtc}
                      required
                    />
                  </div>
                  <div className="relative">
                    <FiMail
                      className="absolute left-3 top-3 text-white/60"
                      aria-hidden="true"
                    />
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={tt.fields.email.label}
                      type="email"
                      className="w-full pl-10 pr-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60 disabled:opacity-50"
                      disabled={!selectedSlotUtc}
                      required
                    />
                  </div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={tt.fields.message.label}
                    rows={4}
                    className="w-full px-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60 disabled:opacity-50"
                    disabled={!selectedSlotUtc}
                    required
                  />
                </div>

                <button
                  onClick={submit}
                  disabled={!readyToBook || submitting}
                  className="mt-5 inline-flex items-center justify-center gap-2 bg-[#846dff] hover:bg-[#5b3eff] transition text-white font-medium px-4 py-2 rounded-md disabled:opacity-40 disabled:cursor-not-allowed w-full"
                >
                  <FiCheck /> {submitting ? tt.booking : tt.bookButton}
                </button>

                <button
                  onClick={() => {
                    setStep(0);
                    setSelectedDateUtcSod(null);
                    setSelectedSlotUtc(null);
                    setName("");
                    setPhone("");
                    setEmail("");
                    setNotes("");
                  }}
                  className="mt-3 text-xs text-white/60 hover:text-white"
                >
                  {tt.reset}
                </button>
              </div>
            </div>
          )}

          {/* ---------- MOBILE: Step 0 (date + time) ---------- */}
          {step === 0 && (
            <div className="grid lg:hidden gap-6">
              {/* Calendar */}
              <div
                className="bg-[#1c212b] border border-white/10 rounded-xl p-4"
                role="grid"
                aria-label={tt.calendarLabel}
              >
                <div className="flex items-center justify-between">
                  <button
                    className="p-2 rounded-md hover:bg-white/5 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                    onClick={prevMonth}
                    disabled={!canGoPrev}
                    aria-label={tt.previousMonth}
                  >
                    <FiChevronLeft aria-hidden="true" />
                  </button>
                  <div className="text-sm font-medium text-white">
                    {headerLabel}
                  </div>
                  <button
                    className="p-2 rounded-md hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                    onClick={nextMonth}
                    aria-label={tt.nextMonth}
                  >
                    <FiChevronRight aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-7 gap-1 text-center text-xs text-white/70">
                  {[tt.weekdays.mon, tt.weekdays.tue, tt.weekdays.wed, tt.weekdays.thu, tt.weekdays.fri, tt.weekdays.sat, tt.weekdays.sun].map(
                    (w) => (
                      <div key={w} className="py-1">
                        {w}
                      </div>
                    )
                  )}
                </div>

                <div className="mt-2 grid grid-cols-7 gap-1">
                  {matrix.map((cell) => {
                    const isSelected = selectedDateUtcSod
                      ? sameDayInTz(selectedDateUtcSod, cell.date, timezone)
                      : false;

                    const base =
                      "relative h-10 rounded-md text-sm select-none flex items-center justify-center transition";
                    const inMonth = cell.inCurrentMonth;
                    const past = cell.isPast && !isSelected;
                    const today = cell.isToday;

                    return (
                      <button
                        key={cell.date.toISOString()}
                        disabled={past}
                        aria-pressed={isSelected}
                        onClick={() => handleDateSelect(cell.date)}
                        className={[
                          base,
                          inMonth ? "text-white/90" : "text-white/50",
                          past
                            ? "opacity-30 cursor-not-allowed"
                            : "hover:bg-[#846dff]/10",
                          isSelected
                            ? "bg-[#846dff]/20 ring-1 ring-[#846dff]"
                            : "border border-white/10",
                          today && !isSelected ? "border-[#846dff]/40" : "",
                        ].join(" ")}
                      >
                        <span className="z-10">
                          {moment(cell.date).tz(timezone).format("D")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time slots */}
              <div className="bg-[#1c212b] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-white">
                    {tt.availableTimes}
                  </div>
                  <div className="text-xs text-white/70">
                    {minutesToLabel(duration)} {tt.each}
                  </div>
                </div>

                {!selectedDateUtcSod && (
                  <div className="mt-6 text-white/75 text-sm">
                    {tt.pickDateHint}
                  </div>
                )}

                {!!selectedDateUtcSod && (
                  <div className="mt-4 max-h-[420px] p-2 overflow-y-auto pr-1 space-y-2 no-scrollbar">
                    {visibleSlotsUtc.map((slot) => {
                      const isActive =
                        selectedSlotUtc?.getTime() === slot.getTime();
                      return (
                        <button
                          key={slot.getTime()}
                          onClick={() => setSelectedSlotUtc(slot)}
                          className={[
                            "w-full justify-between flex items-center rounded-md px-4 py-3 text-sm transition",
                            "border border-white/10 hover:bg-[#846dff]/10",
                            isActive
                              ? "bg-[#846dff]/20 ring-1 ring-[#846dff]"
                              : "",
                          ].join(" ")}
                        >
                          <span className="text-white">
                            {moment(slot).tz(timezone).format("h:mm A")}
                          </span>
                          <span className="text-xs text-white/70">
                            {isActive ? tt.selected : tt.confirm}
                          </span>
                        </button>
                      );
                    })}
                    {visibleSlotsUtc.length === 0 && (
                      <div className="text-sm text-white/70 px-2 py-3">
                        {tt.noFutureSlots.replace("{timezone}", timezone)}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    disabled={!readyToConfirm}
                    onClick={handleConfirmContinue}
                    className="inline-flex items-center gap-2 bg-[#846dff] hover:bg-[#5b3eff] text-white font-medium px-4 py-2 rounded-md disabled:opacity-40"
                  >
                    {tt.continueLabel}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---------- MOBILE: Step 1 (details) ---------- */}
          {step === 1 && selectedSlotUtc && (
            <div className="grid lg:hidden gap-6">
              <div className="bg-[#1c212b] border border-white/10 rounded-xl p-4">
                <p className="text-sm font-medium mb-2 text-white">
                  {tt.youSelected}
                </p>
                <div className="rounded-md bg-[#14171f] border border-white/10 px-3 py-3 text-sm text-white/90">
                  {selectedFullLabel}
                </div>
                <p className="mt-3 text-xs text-white/70">
                  {tt.timezoneLabel}: {timezone}
                </p>
              </div>

              <div className="bg-[#1c212b] border border-white/10 rounded-xl p-4">
                <p className="text-sm font-medium mb-4 text-white">
                  {tt.enterYourDetails}
                </p>
                <div className="grid gap-3">
                  <div className="relative">
                    <FiUser
                      className="absolute left-3 top-3 text-white/60"
                      aria-hidden="true"
                    />
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={tt.fields.name.label}
                      className="w-full pl-10 pr-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                      required
                    />
                  </div>
                  <div className="relative">
                    <FiUser
                      className="absolute left-3 top-3 text-white/60"
                      aria-hidden="true"
                    />
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={tt.fields.phone.label}
                      className="w-full pl-10 pr-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                      required
                    />
                  </div>
                  <div className="relative">
                    <FiMail
                      className="absolute left-3 top-3 text-white/60"
                      aria-hidden="true"
                    />
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={tt.fields.email.label}
                      type="email"
                      className="w-full pl-10 pr-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                      required
                    />
                  </div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={tt.fields.message.label}
                    rows={4}
                    className="w-full px-3 py-2 rounded-md bg-[#14171f] border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#846dff]/60"
                    required
                  />
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <button
                    onClick={() => setStep(0)}
                    className="text-sm text-white/70 hover:text-white"
                  >
                    {tt.changeTime}
                  </button>
                  <button
                    onClick={submit}
                    disabled={
                      !name.trim() ||
                      !email.trim() ||
                      !phone.trim() ||
                      !notes.trim() ||
                      submitting
                    }
                    className="inline-flex items-center gap-2 bg-[#846dff] hover:bg-[#5b3eff] text-white font-medium px-4 py-2 rounded-md disabled:opacity-40"
                  >
                    <FiCheck /> {submitting ? tt.booking : tt.bookButton}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---------- Success ---------- */}
          {step === 2 && (
            <div className="bg-[#1c212b] border border-white/10 rounded-xl p-8 text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-[rgba(132,109,255,0.15)] text-[#846dff] flex items-center justify-center">
                <FiCheck size={24} aria-hidden="true" />
              </div>
              <h4 className="mt-4 text-lg font-semibold text-white">
                {tt.meetingConfirmed}
              </h4>
              <p className="mt-2 text-white/80 text-sm">
                {tt.confirmedMessage}
              </p>
              <button
                onClick={() => {
                  setStep(0);
                  setSelectedDateUtcSod(null);
                  setSelectedSlotUtc(null);
                  setName("");
                  setEmail("");
                  setNotes("");
                }}
                className="mt-6 bg-[#846dff] hover:bg-[#5b3eff] text-white font-medium px-4 py-2 rounded-md"
              >
                {tt.bookAnother}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BookMeeting;
